import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import uniqid from 'uniqid';
import Case from 'case';
import { Box, Text } from 'grommet';
import { CurrentDateContext, useAppTheme } from '@Components/Context';
import { withProductAuth } from '@Components/Layout';
import {
  Seo,
  RadioButtonSlider,
  AppButton,
  Spinning,
  UnderConstruction,
  SubmissionLoader,
} from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  ProductFormFieldContainer,
  ProductFormDropdownSelect,
  ProductFormSectionWrapper,
} from '@Components/Partial/Product/NewSearch';
import {
  initiateNewFormConfigRequest,
  clearNewFormConfigRequest,
  submitNewSearchRequest,
  initiateNewBulkFormConfigRequest,
  clearNewBulkFormConfigRequest,
  submitNewBulkSearchRequest,
} from '@Actions';
import { paths } from '@Components/configs';
import DynamicFormRenderer from './DynamicFormRenderer';

const FormContents = ({
  small,
  loading,
  underMaintenance,
  config,
  searchTypeDisplay,
  requiredFormInputs,
  optionalFormInputs,
  formValues,
  handleSearchType,
  handleFormValues,
  minStartDate,
  timeZone,
  setTimeZone,
  searchTypeKey,
  resetForm,
  submitEnabled,
  handleFormSubmit,
  platformOptionOverrides = null,
  isBulkSearch = false,
  uploadMethod = 'csv',
  onUseCatalogChange = null,
  companyId = null,
  onUploadStart = null,
  onUploadEnd = null,
}) => {
  const {
    theme: { page, button, navigation },
    constructionImage,
  } = useAppTheme();

  if (underMaintenance) {
    return (
      <Box flex align="center" justify="center" background={page.background}>
        <UnderConstruction
          imageUrl={constructionImage}
          buttonHighlight={button.primaryBg}
          header="Under Maintenance"
          message="System currently undergoing maintenance. Please check back later."
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box flex align="center" justify="center" background={page.background}>
        <Spinning size="large" color={page.highlight} />
      </Box>
    );
  }

  return (
    <Box
      flex
      direction={small ? 'column' : 'row'}
      gap="1.5rem"
      justify={small ? 'start' : 'evenly'}
      pad="1.5rem"
      width="100%"
    >
      <ProductFormSectionWrapper
        small={small}
        sectionBg={page.altBg}
        sectionTitle="Required Fields"
        titleColor={page.primaryText}
        borderColor={navigation.border}
        boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
      >
        {Array.isArray(config?.searchTypes) && config.searchTypes.length > 1 && (
          <ProductFormFieldContainer
            small={small}
            fieldTitle="Search Type"
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormDropdownSelect
              selectable
              autoSelect
              label={searchTypeDisplay}
              handleFormValues={(searchTypeName) => handleSearchType(searchTypeName)}
              options={config && config.searchTypes}
            />
          </ProductFormFieldContainer>
        )}
        {Array.isArray(requiredFormInputs)
          && requiredFormInputs.map((dt) => (
            <DynamicFormRenderer
              key={dt.key}
              resetKey={formValues.resetKey}
              searchTypeKey={searchTypeKey}
              fieldData={dt}
              small={small}
              handleFormValues={(value, key) => handleFormValues(value, key)}
              formValues={formValues.searchFieldValues}
              minStartDate={minStartDate}
              timeZone={timeZone}
              setTimeZone={setTimeZone}
              platformOptionOverrides={platformOptionOverrides}
              isBulkSearch={isBulkSearch}
              useCatalog={uploadMethod === 'catalog'}
              onUseCatalogChange={onUseCatalogChange}
              companyId={companyId}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
            />
          ))}
      </ProductFormSectionWrapper>
      <Box gap="2rem" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
        <ProductFormSectionWrapper
          small={small}
          sectionBg={page.altBg}
          sectionTitle="Optional Fields"
          titleColor={page.primaryText}
          borderColor={navigation.border}
        >
          {Array.isArray(optionalFormInputs) && optionalFormInputs?.length > 0 ? (
            optionalFormInputs.map((dt) => (
              <DynamicFormRenderer
                key={dt.key}
                resetKey={formValues.resetKey}
                searchTypeKey={searchTypeKey}
                fieldData={dt}
                small={small}
                handleFormValues={(value, key) => handleFormValues(value, key)}
                formValues={formValues.searchFieldValues}
                minStartDate={minStartDate}
                timeZone={timeZone}
                setTimeZone={setTimeZone}
                platformOptionOverrides={platformOptionOverrides}
                companyId={companyId}
                onUploadStart={onUploadStart}
                onUploadEnd={onUploadEnd}
              />
            ))
          ) : (
            <Box width="100%" align="center" justify="center" pad={{ top: '1rem' }}>
              <Text size="1rem" color={page.primaryText} textAlign="center">
                No optional inputs for current selections
              </Text>
            </Box>
          )}
        </ProductFormSectionWrapper>
        <Box direction="row" gap="1rem" justify="center">
          <AppButton
            width="6.5rem"
            onClick={() => resetForm()}
            level="dynamicLarge"
            color={button.primaryBg}
            fontWeight={600}
            label="Cancel"
          />
          <AppButton
            overrideHover
            disabled={!submitEnabled}
            onClick={(e) => (submitEnabled ? handleFormSubmit(e) : null)}
            level="dynamicLarge"
            color={button.primaryText}
            bgColor={button.primaryBg}
            fontWeight={600}
            label="Start Analysis"
          />
        </Box>
      </Box>
    </Box>
  );
};

FormContents.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  underMaintenance: PropTypes.bool.isRequired,
  config: PropTypes.shape({
    searchTypes: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        fieldData: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired,
            config: PropTypes.objectOf(PropTypes.any),
            required: PropTypes.bool,
            fieldName: PropTypes.string.isRequired,
            fieldType: PropTypes.string.isRequired,
            selectable: PropTypes.bool,
            description: PropTypes.string,
          }).isRequired,
        ).isRequired,
      }),
    ),
  }),
  searchTypeDisplay: PropTypes.string,
  requiredFormInputs: PropTypes.arrayOf(PropTypes.any),
  optionalFormInputs: PropTypes.arrayOf(PropTypes.any),
  formValues: PropTypes.shape({
    searchType: PropTypes.string,
    searchFieldValues: PropTypes.objectOf(PropTypes.any),
    resetKey: PropTypes.string,
  }),
  handleSearchType: PropTypes.func.isRequired,
  handleFormValues: PropTypes.func.isRequired,
  minStartDate: PropTypes.instanceOf(Date),
  timeZone: PropTypes.string.isRequired,
  setTimeZone: PropTypes.func.isRequired,
  searchTypeKey: PropTypes.string,
  resetForm: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool,
  handleFormSubmit: PropTypes.func.isRequired,
  platformOptionOverrides: PropTypes.arrayOf(PropTypes.string),
  isBulkSearch: PropTypes.bool,
  uploadMethod: PropTypes.string,
  onUseCatalogChange: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
};

const MEDIA_FIELD_TYPES = ['SINGLE_MEDIA_UPLOAD', 'MULTIPLE_MEDIA_UPLOAD', 'file'];

const NewSearchPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchFormConfig,
  clearFormConfig,
  submitNewSearch,
  fetchBulkFormConfig,
  clearBulkFormConfig,
  submitNewBulkSearch,
  companyId,
  paymentPending = false,
  formConfig = null,
  bulkFormConfig = null,
  featureConfig = null,
  searchDisabled = false,
}) => {
  const [bulkSearch, setBulkSearch] = React.useState(false);
  const [searchType, setSearchType] = React.useState(null);
  const [useConfig, setUseConfig] = React.useState(null);
  const [searchTypeKey, setSearchTypeKey] = React.useState(null);
  const [formConfigResult, setFormConfigResult] = React.useState(null);
  const [requiredFormInputs, setRequiredFormInputs] = React.useState(null);
  const [optionalFormInputs, setOptionalFormInputs] = React.useState(null);
  const [configLoading, setConfigLoading] = React.useState(true);
  const [formValues, setFormValues] = React.useState({
    searchType: null,
    searchFieldValues: {},
    resetKey: uniqid(),
  });
  const [selectedTimeZone, setSelectedTimeZone] = React.useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [uploadMethod, setUploadMethod] = React.useState('csv');
  const [uploadingCount, setUploadingCount] = React.useState(0);
  const { today } = React.useContext(CurrentDateContext);
  const {
    theme: { page }, disableSearchFeatures, isDarkMode,
  } = useAppTheme();
  const underMaintenance = disableSearchFeatures || searchDisabled;

  React.useEffect(() => {
    fetchFormConfig();
    fetchBulkFormConfig();

    return () => {
      clearFormConfig();
      clearBulkFormConfig();
    };
  }, []);

  React.useEffect(() => {
    if (bulkSearch && bulkFormConfig) {
      setUseConfig(bulkFormConfig);
      setSearchType(bulkFormConfig.searchTypes[0].name);
    } else if (formConfig) {
      setUseConfig(formConfig);
      setSearchType(formConfig.searchTypes[0].name);
    }
  }, [formConfig, bulkFormConfig, bulkSearch]);

  React.useEffect(() => {
    if (useConfig && searchType) {
      setFormConfigResult(_.find(useConfig.searchTypes, { name: searchType }).fieldData);
      setFormValues({ searchType, searchFieldValues: {}, resetKey: uniqid() });
      setSearchTypeKey(Case.camel(_.find(useConfig.searchTypes, { name: searchType })?.category));
    }
  }, [searchType, bulkSearch, useConfig]);

  React.useEffect(() => {
    if (formConfigResult) {
      const partitionedResults = _.partition(formConfigResult, 'required');
      const [requiredInputs, optionalInputs] = partitionedResults;

      const filteredRequiredInputs = _.filter(
        requiredInputs,
        ({ config }) => !config?.supportedCategoryPlatforms && !config?.supportedRecurrence,
      );
      const filteredOptionalInputs = _.filter(
        optionalInputs,
        ({ config }) => !config?.supportedCategoryPlatforms && !config?.supportedRecurrence,
      );

      setRequiredFormInputs(filteredRequiredInputs);
      setOptionalFormInputs(filteredOptionalInputs);
      setConfigLoading(false);
    }
  }, [formConfigResult]);

  const platformOptionOverrides = featureConfig?.enabledSearchPlatforms;

  const checkInputRequirements = (config) => {
    const selectedPlatforms = formValues.searchFieldValues.platform;
    const selectedRecurrence = formValues.searchFieldValues.recurrence;
    let currentPlatformConfig = config?.supportedCategoryPlatforms
      && config.supportedCategoryPlatforms[searchTypeKey];

    if (platformOptionOverrides?.length > 0) {
      currentPlatformConfig = platformOptionOverrides;
    }

    if (currentPlatformConfig && config?.supportedRecurrence) {
      return (
        selectedPlatforms?.some((p) => currentPlatformConfig.includes(p))
        && config.supportedRecurrence === selectedRecurrence
      );
    }

    if (currentPlatformConfig && selectedPlatforms?.length > 0) {
      return selectedPlatforms?.some((p) => currentPlatformConfig.includes(p));
    }

    if (config?.supportedRecurrence) {
      return config.supportedRecurrence === selectedRecurrence;
    }

    return true;
  };

  React.useEffect(() => {
    if (!formConfigResult) return;

    // Partition and filter inputs based on requirements
    const partitionedResults = _.partition(formConfigResult, 'required');
    const [requiredInputs, optionalInputs] = partitionedResults;
    const filteredRequiredInputs = _.filter(
      requiredInputs,
      ({ config }) => checkInputRequirements(config),
    );
    const filteredOptionalInputs = _.filter(
      optionalInputs,
      ({ config }) => checkInputRequirements(config),
    );

    setRequiredFormInputs(filteredRequiredInputs);
    setOptionalFormInputs(filteredOptionalInputs);

    // Remove unsupported keys from formValues, but preserve valid ones
    const requiredKeys = filteredRequiredInputs.map(({ key }) => key);
    const optionalKeys = filteredOptionalInputs.map(({ key }) => key);
    const allowedKeys = [...requiredKeys, ...optionalKeys];
    const selectedKeys = Object.keys(formValues.searchFieldValues);
    // Preserve use_catalog flag (not in backend config but needed for API)
    const unsupportedKeys = selectedKeys.filter((k) => !allowedKeys.includes(k) && k !== 'use_catalog');

    if (unsupportedKeys.length > 0) {
      setFormValues((prev) => {
        const updatedValues = { ...prev.searchFieldValues };
        unsupportedKeys.forEach((key) => {
          delete updatedValues[key];
        });

        if (Object.keys(updatedValues).length !== selectedKeys.length) {
          return { ...prev, searchFieldValues: updatedValues };
        }
        return prev;
      });
    }
  }, [formConfigResult, formValues.searchFieldValues]);

  const handleFormValues = (value, key) => {
    setFormValues((prev) => {
      const updatedValues = { ...prev.searchFieldValues };
      if (!value || value?.length === 0) {
        delete updatedValues[key];
        return { ...prev, searchFieldValues: updatedValues };
      }
      updatedValues[key] = value;
      return { ...prev, searchFieldValues: updatedValues };
    });
  };

  const handleSearchType = (searchTypeName) => {
    setFormValues({ ...formValues, searchFieldValues: {}, resetKey: uniqid() });
    setSearchType(searchTypeName);
  };

  const resetForm = () => setFormValues({ searchFieldValues: {}, resetKey: uniqid() });

  const handleUploadStart = () => setUploadingCount((c) => c + 1);
  const handleUploadEnd = () => setUploadingCount((c) => Math.max(0, c - 1));

  const handleBulkSelect = () => {
    resetForm();
    setUploadMethod('csv'); // Reset to CSV when switching modes
    setBulkSearch(!bulkSearch);
  };

  const handleUseCatalogChange = (useCatalog) => {
    setUploadMethod(useCatalog ? 'catalog' : 'csv');

    setFormValues((prevFormValues) => {
      const updatedValues = { ...prevFormValues.searchFieldValues };

      // Remove file fields when switching to catalog
      if (useCatalog && requiredFormInputs) {
        const fileFields = requiredFormInputs
          .filter((field) => MEDIA_FIELD_TYPES.includes(field.fieldType))
          .map((field) => field.key);

        fileFields.forEach((key) => {
          delete updatedValues[key];
        });
      }

      // Add or remove use_catalog flag
      if (useCatalog) {
        /* eslint-disable-next-line camelcase */
        updatedValues.use_catalog = true;
      } else {
        /* eslint-disable-next-line camelcase */
        delete updatedValues.use_catalog;
      }

      return { ...prevFormValues, searchFieldValues: updatedValues };
    });
  };

  const searchTypeDisplay = useConfig
    && (searchType
      ? _.find(useConfig.searchTypes, { name: searchType }).name
      : useConfig.searchTypes[0].name);
  const searchTypeId = useConfig
    && (searchType
      ? _.find(useConfig.searchTypes, { name: searchType }).id
      : useConfig.searchTypes[0].id);

  const requiredKeys = Array.isArray(requiredFormInputs)
    ? requiredFormInputs.map(({ key }) => key)
    : [];

  // Filter out file/CSV fields from required validation when catalog is selected
  const filteredRequiredKeys = bulkSearch && uploadMethod === 'catalog'
    ? requiredKeys.filter((key) => {
      const field = requiredFormInputs.find((f) => f.key === key);
      return !MEDIA_FIELD_TYPES.includes(field?.fieldType);
    })
    : requiredKeys;

  const submitEnabled = !paymentPending
    && uploadingCount === 0
    && (filteredRequiredKeys.length === 0
    || filteredRequiredKeys.every(
      (key) => key in formValues.searchFieldValues
        && (formValues.searchFieldValues[key].length > 0
          || formValues.searchFieldValues[key] !== null),
    ));

  /* eslint-disable camelcase */
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (paymentPending) return;

    if (bulkSearch) {
      submitNewBulkSearch(
        {
          search: {
            search_type_id: searchTypeId,
            search_field_values: formValues.searchFieldValues,
          },
        },
        paths.productListSearches.replace(':status', 'all'),
        () => resetForm(),
      );
    } else {
      submitNewSearch(
        {
          search: {
            search_type_id: searchTypeId,
            search_field_values: formValues.searchFieldValues,
          },
        },
        paths.productListSearches.replace(':status', 'all'),
        () => resetForm(),
      );
    }
  };
  /* eslint-enable camelcase */

  const pageTitle = bulkSearch ? 'Create bulk searches' : 'Create a new search';
  const submitMessage = bulkSearch ? 'Creating bulk searches...' : 'Creating search...';

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: searchTypeDisplay ? `${searchTypeDisplay}` : null,
        bannerChildrenPosition: 'bottom',
        bannerChildren: bulkFormConfig?.searchTypes?.length > 0 && (
          <Box direction="row" justify="between">
            <RadioButtonSlider
              radioOptions={[
                { label: 'Individual', value: 'individual' },
                { label: 'Bulk search', value: 'bulk' },
              ]}
              componentBg={page.altBg}
              textColor={page.primaryText}
              activeButtonBg={isDarkMode ? page.highlight : 'white'}
              updateParent={() => handleBulkSelect()}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={page.highlight}
          message={submitMessage}
          contentWidth="20rem"
        />
      )}
      <FormContents
        small={small}
        key={bulkSearch ? 'bulk' : 'individual'}
        loading={loading || configLoading}
        underMaintenance={underMaintenance}
        config={useConfig}
        searchTypeDisplay={searchTypeDisplay}
        requiredFormInputs={requiredFormInputs}
        optionalFormInputs={optionalFormInputs}
        formValues={formValues}
        handleSearchType={handleSearchType}
        handleFormValues={handleFormValues}
        minStartDate={today}
        timeZone={selectedTimeZone}
        setTimeZone={setSelectedTimeZone}
        searchTypeKey={searchTypeKey}
        resetForm={resetForm}
        submitEnabled={submitEnabled && !submitLoading}
        handleFormSubmit={handleFormSubmit}
        platformOptionOverrides={platformOptionOverrides}
        isBulkSearch={bulkSearch}
        uploadMethod={uploadMethod}
        onUseCatalogChange={searchTypeKey?.includes('publisher') ? handleUseCatalogChange : null}
        companyId={!bulkSearch ? companyId : null}
        onUploadStart={!bulkSearch ? handleUploadStart : null}
        onUploadEnd={!bulkSearch ? handleUploadEnd : null}
      />
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    formConfig: state.productNewForm.formConfig,
    bulkFormConfig: state.productNewForm.bulkFormConfig,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    companyId: state.auth.companyId,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchFormConfig: initiateNewFormConfigRequest,
      clearFormConfig: clearNewFormConfigRequest,
      submitNewSearch: submitNewSearchRequest,
      fetchBulkFormConfig: initiateNewBulkFormConfigRequest,
      clearBulkFormConfig: clearNewBulkFormConfigRequest,
      submitNewBulkSearch: submitNewBulkSearchRequest,
    },
    dispatch,
  );
}

NewSearchPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchFormConfig: PropTypes.func.isRequired,
  clearFormConfig: PropTypes.func.isRequired,
  submitNewSearch: PropTypes.func.isRequired,
  fetchBulkFormConfig: PropTypes.func.isRequired,
  clearBulkFormConfig: PropTypes.func.isRequired,
  submitNewBulkSearch: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  formConfig: PropTypes.shape({
    searchTypes: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        fieldData: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired,
            config: PropTypes.objectOf(PropTypes.any),
            required: PropTypes.bool,
            fieldName: PropTypes.string.isRequired,
            fieldType: PropTypes.string.isRequired,
            selectable: PropTypes.bool,
            description: PropTypes.string,
          }).isRequired,
        ).isRequired,
      }),
    ),
  }),
  bulkFormConfig: PropTypes.shape({
    searchTypes: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        fieldData: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired,
            config: PropTypes.objectOf(PropTypes.any),
            required: PropTypes.bool,
            fieldName: PropTypes.string.isRequired,
            fieldType: PropTypes.string.isRequired,
            selectable: PropTypes.bool,
            description: PropTypes.string,
          }).isRequired,
        ).isRequired,
      }),
    ),
  }),
  featureConfig: PropTypes.objectOf(PropTypes.any),
  searchDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(NewSearchPage));
